import { useAuthStore } from "../state/auth";

export interface ScanMapPoint {
  id: string;
  gps_lat: number;
  gps_lng: number;
  scanned_at: string;
  hospital_id: string | null;
  hospital_name: string;
  location_name: string;
  scanner_id: string | null;
  officer_id?: string | null;
  officer_name?: string | null;
  /** True when this scan is flagged anomalous (impossible movement and/or
   *  plotted far from its own hospital). */
  anomalous?: boolean;
  /** Why it's anomalous: 'movement' (impossible officer travel), 'hospital'
   *  (plotted far from its labeled hospital), or 'both'. */
  anomaly_reason?: "movement" | "hospital" | "both" | null;
  /** 'EVENT' | 'CHECKPOINT' | 'MISPLACED' — where the plotted point came from. */
  gps_source?: string;
  /** For misplaced scans: the checkpoint anchor the scan should have been at. */
  expected_lat?: number | null;
  expected_lng?: number | null;
  /** For misplaced scans: how far the reported GPS is from that anchor, in meters. */
  distance_m?: number | null;
}

interface ScanMapResponse {
  scans: ScanMapPoint[];
  count: number;
  total: number;
  view?: "onsite" | "misplaced" | "anomalous";
  onsite?: number;
  misplaced?: number;
  anomalous?: number;
  total_all?: number;
  unlocated?: number;
  limit: number;
  limited: boolean;
}

export interface ScanMapResult {
  /** The scan points returned (capped at `limit`). */
  scans: ScanMapPoint[];
  /** Total matching scans in the current view, before the limit was applied. */
  total: number;
  /** Count of on-site (normally plotted) scans, regardless of the current view. */
  onsite: number;
  /** Count of misplaced outliers, regardless of the current view. */
  misplaced: number;
  /** Count of anomalous-movement scans, regardless of the current view. */
  anomalous: number;
  /** The row cap that was applied to this query. */
  limit: number;
  /** True when `total` exceeds the number of returned scans. */
  limited: boolean;
}

export interface FetchScanMapParams {
  hospitalId?: string;
  /** Restrict to scans attributed to one officer (officer scan-history map). */
  officerId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  /** 'onsite' (default) plots normal scans; 'misplaced' returns the outliers;
   *  'anomalous' returns only scans flagged as impossible officer movement. */
  view?: "onsite" | "misplaced" | "anomalous";
}

/**
 * Fetches GPS-tagged scan events for the live Scan Map, scoped server-side to
 * the hospitals the current user is allowed to view.
 */
export async function fetchScanMap(params: FetchScanMapParams = {}): Promise<ScanMapResult> {
  const { user, token } = useAuthStore.getState();
  if (!user?.email) {
    throw new Error("User not authenticated");
  }

  const url = new URL("/api/scans/map", window.location.origin);
  if (params.hospitalId) url.searchParams.append("hospitalId", params.hospitalId);
  if (params.officerId) url.searchParams.append("officerId", params.officerId);
  if (params.startDate) url.searchParams.append("startDate", params.startDate);
  if (params.endDate) url.searchParams.append("endDate", params.endDate);
  if (params.limit) url.searchParams.append("limit", String(params.limit));
  if (params.view) url.searchParams.append("view", params.view);

  const headers: Record<string, string> = {
    Accept: "application/json",
    "x-user-email": user.email,
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), { headers, cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load scan map (${response.status})`);
  }

  const data = (await response.json()) as ScanMapResponse;
  const scans = data.scans ?? [];
  const total = data.total ?? scans.length;
  return {
    scans,
    total,
    onsite: data.onsite ?? total,
    misplaced: data.misplaced ?? 0,
    anomalous: data.anomalous ?? 0,
    limit: data.limit ?? scans.length,
    limited: data.limited ?? total > scans.length,
  };
}
